import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  ThemeProvider,
  createTheme,
  CssBaseline,
  alpha,
  styled,
  Avatar,
  Divider,
  Grid,
  Stack,
  useMediaQuery,
  Menu,
  MenuItem as MuiMenuItem,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Checkbox,
  Badge,
  Chip,
} from '@mui/material';
import {
  CloudUpload,
  Visibility,
  Description,
  Brightness4,
  Brightness7,
  LocalHospital,
  Search,
  Refresh,
  Logout as LogoutIcon,
  Person,
  Settings,
  Key as KeyIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  ChevronLeft,
  Close,
  Send,
  Email,
  Help,
  Edit,
  Info as InfoIcon,
  Business,
  DoneAll,
  History as HistoryIcon,
  Science as ScienceIcon,
  Schedule,
  AssignmentInd,
  CheckCircle,
  Flag,
  MoreVert,
  ChatBubbleOutline as ChatIcon,
  Lock,
  LockOpen,
} from '@mui/icons-material';
import DirectMessagesView from '../Common/DirectMessagesView';
import { useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { uploadImage } from '../../services/images';
import EnhancedUploadDialog from '../Clinician/EnhancedUploadDialog';
import ChangePasswordDialog from '../Auth/ChangePassword';
import NotificationListener from '../Common/NotificationListener';
import { getUnreadNotifications, markAllNotificationsAsRead } from '../../services/notifications';
import CreateSelfReport from '../Clinician/CreateSelfReport';
import DycovueReportForm from './DycovueReportForm';
import RequestDycovueReader from '../Clinician/RequestDycovueReader';
import SendEmailDialog from '../Clinician/SendEmailDialog';
import EditPatientMetadataDialog from '../Clinician/EditPatientMetadataDialog';
import MessageHistory from '../Clinician/MessageHistory';
import ReportStatusBadge from '../Common/ReportStatusBadge';
import ReportViewer from '../Common/ReportViewer';
import PatientHistoryPanel from '../Common/PatientHistoryPanel';
import { getScanBoardStudies, syncOrthanc } from '../../services/pacs';
import { getStudyStatistics } from '../../services/analytics';
import FullLogo from '../logo/FullLogo';
import ProfileSettings from '../Admin/ProfileSettings';
import UserSettings from '../UserSettings';
import HelpSupport from '../Admin/HelpSupport';
import { formatDicomDate, generateOHIFUrl, formatTatMinutes } from '../../services/utils';
import RequestLabTestDialog from './RequestLabTestDialog';
import LabReportModal from './LabReportModal';
import { labReportService } from '../../services/lis';
import ClaimStudyModal from '../Common/ClaimStudyModal';
import { claimStudy, unclaimStudy, getMyRadiologistStats } from '../../services/reporting';

// Helper function to format DICOM patient names (replace ^ separator with space)
const formatPatientName = (name) => {
  if (!name) return 'Unknown Patient';
  return name.replace(/\^/g, ' ').trim() || 'Unknown Patient';
};

// Tesla-inspired color palette
const teslaColors = {
  dark: {
    primary: '#3E6AE1',
    secondary: '#34C3FF',
    background: '#121212',
    surface: '#1E1E1E',
    card: 'rgba(30, 30, 30, 0.8)',
    text: '#FFFFFF',
    textSecondary: 'rgba(255, 255, 255, 0.7)',
    border: 'rgba(255, 255, 255, 0.12)',
  },
  light: {
    primary: '#3E6AE1',
    secondary: '#34C3FF',
    background: '#F5F5F7',
    surface: '#FFFFFF',
    card: 'rgba(255, 255, 255, 0.8)',
    text: '#121212',
    textSecondary: 'rgba(18, 18, 18, 0.7)',
    border: 'rgba(0, 0, 0, 0.12)',
  }
};

// Styled icon button with modern effects
const ModernIconButton = styled(IconButton)(({ theme, variant = 'default' }) => {
  const gradients = {
    view: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    report: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    selfReport: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    inProgress: 'linear-gradient(135deg, #f6a623 0%, #ffb74d 100%)',
    dycovue: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    email: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    edit: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    info: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
    disabled: 'linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%)',
  };

  // Solid fallback colors (one per variant) used to show color by default
  const solidColors = {
    view: '#667eea',
    report: '#f5576c',
    selfReport: '#4facfe',
    inProgress: '#f6a623',
    dycovue: '#43e97b',
    email: '#fa709a',
    edit: '#30cfd0',
    info: '#2193b0',
    disabled: '#95a5a6',
  };

  const gradient = gradients[variant] || gradients.view;
  const solid = solidColors[variant] || solidColors.view;

  return {
    position: 'relative',
    width: 26,
    height: 26,
    padding: '2px',
    borderRadius: '4px',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    transition: 'all 0.15s ease',
    '& .MuiSvgIcon-root': {
      fontSize: '0.9rem',
      color: solid,
      transition: 'all 0.15s ease',
    },
    '&:hover': {
      background: gradient,
      transform: 'scale(1.05)',
      boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
      border: `1px solid ${alpha('#fff', 0.2)}`,
      '& .MuiSvgIcon-root': {
        WebkitTextFillColor: '#fff',
        color: '#fff',
      },
    },
    '&:active': {
      transform: 'scale(0.98)',
    },
    '&.Mui-disabled': {
      backgroundColor: alpha(theme.palette.action.disabledBackground, 0.3),
      border: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
      '& .MuiSvgIcon-root': {
        color: solidColors.disabled,
      },
    },
  };
});

// Performant surface styled components
const GlassPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
  borderRadius: '6px',
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.08)' 
    : 'rgba(0, 0, 0, 0.08)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 1px 3px rgba(0, 0, 0, 0.25)'
    : '0 1px 3px rgba(0, 0, 0, 0.05)',
  backgroundImage: 'none',
}));

const GlowButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${teslaColors.dark.primary}, ${teslaColors.dark.secondary})`,
  border: `1.5px solid ${teslaColors.dark.primary}`,
  borderRadius: '8px',
  color: 'white',
  padding: '6px 16px',
  boxShadow: `0 0 10px ${alpha(teslaColors.dark.primary, 0.4)}`,
  transition: 'all 0.25s ease',
  '&:hover': {
    boxShadow: `0 0 15px ${alpha(teslaColors.dark.primary, 0.7)}`,
    transform: 'translateY(-1px)',
    border: `1.5px solid ${teslaColors.dark.secondary}`,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '6px 12px',
    fontSize: '0.8rem',
  },
}));

const drawerWidth = 260;
const collapsedDrawerWidth = 68;

const StyledDrawer = styled(Drawer)(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedDrawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    width: open ? drawerWidth : collapsedDrawerWidth,
    height: '100%',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    background: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isDark' && prop !== 'drawerOpen',
})(({ theme, selected, isDark, drawerOpen }) => ({
  borderRadius: '8px',
  margin: drawerOpen ? '4px 10px' : '4px auto',
  marginBottom: '6px',
  padding: drawerOpen ? '10px 14px' : '10px 0',
  width: drawerOpen ? 'calc(100% - 20px)' : '44px',
  height: '44px',
  minHeight: '44px',
  justifyContent: drawerOpen ? 'initial' : 'center',
  alignItems: 'center',
  transition: 'all 0.2s ease',
  boxSizing: 'border-box',
  '&:hover': {
    backgroundColor: isDark 
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.primary.main, 0.08),
  },
  ...(selected && {
    backgroundColor: isDark 
      ? alpha(theme.palette.primary.main, 0.22)
      : theme.palette.primary.main,
    color: isDark ? theme.palette.primary.main : 'white',
    '& .MuiListItemIcon-root': {
      color: isDark ? theme.palette.primary.main : 'white',
    },
    '&:hover': {
      backgroundColor: isDark 
        ? alpha(theme.palette.primary.main, 0.28)
        : theme.palette.primary.dark,
    },
  }),
}));

// Patient Detail Dialog Component
const PatientDetailDialog = ({ open, onClose, study, onRequestLabTest, onEditMetadata }) => {
  const theme = useTheme();
  
  if (!study) return null;

  const patient = study.patient || {};
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          width: { xs: '95vw', sm: '760px', md: '780px' },
          maxWidth: '800px !important',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ px: 2, py: 1.25 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.98rem' }}>Patient & Study Details</Typography>
          <IconButton onClick={onClose} size="small">
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2, overflowY: 'auto' }}>
        <Grid container spacing={1.5}>
          {/* Patient Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
              Patient Information
            </Typography>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Patient Name
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {formatPatientName(patient.patientName)}
            </Typography>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Patient ID
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {patient.patientId || 'N/A'}
            </Typography>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Date of Birth
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {patient.patientBirthDate
                ? formatDicomDate(patient.patientBirthDate)
                : 'N/A'}
            </Typography>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Sex
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {patient.patientSex === 'M' ? 'Male' : 
               patient.patientSex === 'F' ? 'Female' : 
               patient.patientSex === 'O' ? 'Other' :
               patient.patientSex || 'Unknown'}
            </Typography>
          </Grid>

          {/* Study Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ fontSize: '0.85rem', mt: 0.5 }}>
              Study Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Study Description
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {study.studyDescription || 'Unnamed Study'}
            </Typography>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Modality
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {study.modality || 'Unknown'}
            </Typography>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Study Date & Time
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {study.studyDate 
                ? new Date(study.studyDate).toLocaleDateString() 
                : 'N/A'}{study.studyTime && study.studyTime !== 'N/A' ? ` • ${study.studyTime}` : ''}
            </Typography>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Accession Number
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {study.accessionNumber || 'N/A'}
            </Typography>
          </Grid>
          
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Referring Physician
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {study.referringPhysicianName || 'N/A'}
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Series / Images
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.88rem' }}>
              {study.seriesCount ?? study.totalSeries ?? 0} series • {study.totalInstances ?? study.instanceCount ?? 0} images
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 500 }}>
              Report Status
            </Typography>
            <Box sx={{ mt: 0.25, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {study.selfReportStatus || study.dycovueReportStatus ? (
                <>
                  {study.selfReportStatus && (
                    <ReportStatusBadge status={study.selfReportStatus} type="self-report" />
                  )}
                  {study.dycovueReportStatus && (
                    <ReportStatusBadge
                      status={study.dycovueReportStatus}
                      type="dycovue-report"
                      isFlagged={
                        study.facilityReviewStatus === 'flagged' ||
                        study.dycovueReport?.facilityReview?.status === 'flagged'
                      }
                    />
                  )}
                </>
              ) : (
                <Typography variant="body2" fontWeight={500} color="textSecondary" sx={{ fontSize: '0.82rem' }}>
                  None
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Technical Details: Study Instance UID */}
          <Grid item xs={12}>
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Study Instance UID:
              </Typography>
              <Typography 
                variant="caption" 
                fontWeight={500} 
                sx={{ 
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  color: 'textSecondary',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                }}
              >
                {study.studyInstanceUID || 'N/A'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 2, pb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ScienceIcon />}
            onClick={() => {
              if (onRequestLabTest) {
                onRequestLabTest(study);
              }
            }}
            sx={{
              fontFamily: '"Satoshi", sans-serif',
              textTransform: 'none',
              borderRadius: '8px',
              color: '#7C3AED',
              borderColor: '#7C3AED',
              fontWeight: 600,
              fontSize: '0.8rem',
              '&:hover': {
                borderColor: '#6D28D9',
                backgroundColor: alpha('#7C3AED', 0.08),
              }
            }}
          >
            Request Lab Test
          </Button>
          {onEditMetadata && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => {
                onClose();
                onEditMetadata(study);
              }}
              sx={{
                fontFamily: '"Satoshi", sans-serif',
                textTransform: 'none',
                borderRadius: '8px',
                color: '#0284C7',
                borderColor: '#0284C7',
                fontWeight: 600,
                fontSize: '0.8rem',
                '&:hover': {
                  borderColor: '#0369A1',
                  backgroundColor: alpha('#0284C7', 0.08),
                }
              }}
            >
              Edit Metadata
            </Button>
          )}
        </Box>
        <Button onClick={onClose} variant="contained" sx={{ borderRadius: '8px', fontSize: '0.8rem', textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Create theme function
const getDesignTokens = (mode) => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: { main: teslaColors.light.primary },
          secondary: { main: teslaColors.light.secondary },
          background: { default: teslaColors.light.background, paper: teslaColors.light.surface },
          text: { primary: teslaColors.light.text, secondary: teslaColors.light.textSecondary },
          divider: teslaColors.light.border,
        }
      : {
          primary: { main: teslaColors.dark.primary },
          secondary: { main: teslaColors.dark.secondary },
          background: { default: teslaColors.dark.background, paper: teslaColors.dark.surface },
          text: { primary: teslaColors.dark.text, secondary: teslaColors.dark.textSecondary },
          divider: teslaColors.dark.border,
        }),
  },
  typography: {
    fontFamily: '"Satoshi", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h6: { fontWeight: 600 },
    body1: { fontWeight: 400, lineHeight: 1.5 },
    body2: { fontWeight: 400 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiButton: { styleOverrides: { root: { textTransform: 'none', borderRadius: '12px', fontWeight: 500 } } },
    MuiTableCell: { styleOverrides: { root: { borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}` } } },
  },
});

// Color mode context
export const ColorModeContext = React.createContext({ toggleColorMode: () => {} });

// Simple debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

// ─── Status tag helpers ──────────────────────────────────────────────────────

const MODALITY_COLORS = {
  CT:  { color: '#f87171', bg: 'rgba(248,113,113,0.1)' },
  MR:  { color: '#c084fc', bg: 'rgba(192,132,252,0.1)' },
  MRI: { color: '#c084fc', bg: 'rgba(192,132,252,0.1)' },
  XR:  { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
  XRAY: { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
  CR:  { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
  DR:  { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
  US:  { color: '#22d3ee', bg: 'rgba(34,211,238,0.1)' },
  MG:  { color: '#fb923c', bg: 'rgba(251,146,60,0.1)' },
  MAMMO: { color: '#fb923c', bg: 'rgba(251,146,60,0.1)' },
  PT:  { color: '#4ade80', bg: 'rgba(74,222,128,0.1)' },
  NM:  { color: '#4ade80', bg: 'rgba(74,222,128,0.1)' },
  OT:  { color: '#334155', bg: 'rgba(148,163,184,0.18)' },
};

const getModalityPalette = (modality) => {
  const key = String(modality || '').trim().toUpperCase();
  return MODALITY_COLORS[key] || MODALITY_COLORS.OT;
};

const ORDER_WORKFLOW_CHIPS = {
  NEW:      { label: 'New',       color: '#94a3b8', bg: 'rgba(148,163,184,0.13)' },
  ARRIVAL:  { label: 'Arrived',   color: '#38bdf8', bg: 'rgba(56,189,248,0.12)' },
  READY:    { label: 'Ready',     color: '#22d3ee', bg: 'rgba(34,211,238,0.12)' },
  STARTED:  { label: 'Scanning',  color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  ENDEXAM:  { label: 'Scanned',   color: '#4ade80', bg: 'rgba(74,222,128,0.12)' },
  QC:       { label: 'QC',        color: '#c084fc', bg: 'rgba(192,132,252,0.12)' },
  TOREPORT: { label: 'To Report', color: '#818cf8', bg: 'rgba(129,140,248,0.12)' },
  PRELIM:   { label: 'Prelim',    color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
};

const TAG = {
  display: 'inline-flex',
  alignItems: 'center',
  px: '4px',
  py: '1px',
  borderRadius: '3px',
  fontSize: '0.55rem',
  fontWeight: 700,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  lineHeight: 1.2,
};

function getStudyStatusChips(study, isClaimedByMe = false, isClaimedByOther = false, assignedName = '') {
  const chips = [];

  if (study.isOrder) {
    chips.push({ label: 'Order', color: '#818cf8', bg: 'rgba(129,140,248,0.15)' });
    const wf = ORDER_WORKFLOW_CHIPS[study.orderStatus || study.status];
    if (wf) chips.push(wf);
    if (study.hasImages || (study.totalInstances || 0) > 0) {
      chips.push({ label: `${study.totalInstances || '?'} img`, color: '#4ade80', bg: 'rgba(74,222,128,0.1)' });
    } else {
      chips.push({ label: 'No Images', color: '#64748b', bg: 'rgba(100,116,139,0.1)' });
    }
  }

  const isFlagged =
    study.dycovueReport?.facilityReview?.status === 'flagged' ||
    study.facilityReviewStatus === 'flagged';
  if (isFlagged) {
    chips.push({ label: 'FLAGGED', color: '#ef4444', bg: 'rgba(239,68,68,0.2)' });
  }
  if (study.dycovueReportStatus === 'completed') {
    chips.push({ label: 'Reported ✓', color: '#4ade80', bg: 'rgba(74,222,128,0.12)' });
  } else if (!isFlagged && study.dycovueReportStatus === 'in_progress') {
    chips.push({ label: 'Reporting', color: '#fb923c', bg: 'rgba(251,146,60,0.12)' });
  } else if (study.dycovueReportStatus === 'claimed' || isClaimedByMe || isClaimedByOther || Boolean(study.assignedRadiologist && study.dycovueReportStatus !== 'completed')) {
    if (isClaimedByMe) {
      chips.push({ label: 'Claimed (You)', color: '#10b981', bg: 'rgba(16,185,129,0.15)', isClaimChip: true });
    } else if (isClaimedByOther) {
      chips.push({ label: 'Claimed', color: '#ef4444', bg: 'rgba(239,68,68,0.15)', isClaimChip: true });
    } else {
      chips.push({ label: 'Claimed', color: '#c084fc', bg: 'rgba(192,132,252,0.15)', isClaimChip: true });
    }
  } else if (study.dycovueReportStatus === 'requested') {
    chips.push({ label: 'Requested', color: '#818cf8', bg: 'rgba(129,140,248,0.12)' });
  }
  if (study.selfReportStatus === 'completed') {
    chips.push({ label: 'Self-Report ✓', color: '#38bdf8', bg: 'rgba(56,189,248,0.12)' });
  } else if (study.selfReportStatus === 'in_progress' || study.selfReportStatus === 'draft') {
    chips.push({ label: 'Draft', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' });
  }

  return chips;
}

// ─────────────────────────────────────────────────────────────────────────────

function TatCountdownChip({ tatDeadline, tatStatus, dycovueReportStatus }) {
  const [now, setNow] = React.useState(Date.now());

  React.useEffect(() => {
    if (!tatDeadline) return;
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, [tatDeadline]);

  if (!tatDeadline) return null;
  const inFlight = ['requested', 'claimed', 'in_progress'].includes(dycovueReportStatus);
  if (!inFlight) return null;

  const msLeft = new Date(tatDeadline).getTime() - now;
  const minLeft = Math.round(msLeft / 60000);

  let label, color, bg;
  if (minLeft < 0) {
    const over = Math.abs(minLeft);
    label = `${over}m overdue`;
    color = '#ef4444';
    bg = 'rgba(239,68,68,0.15)';
  } else if (minLeft <= 30) {
    label = `${minLeft}m left`;
    color = '#f97316';
    bg = 'rgba(249,115,22,0.15)';
  } else {
    label = `${minLeft}m left`;
    color = '#4ade80';
    bg = 'rgba(74,222,128,0.12)';
  }

  return (
    <Box component="span" sx={{ ...TAG, backgroundColor: bg, color, mt: '2px' }}>
      ⏱ {label}
    </Box>
  );
}

// Memoized table row
const StudyRow = React.memo(function StudyRow({
  studyData,
  index,
  t,
  isMobile,
  formatPatientName,
  onViewImages,
  onViewReports,
  onCreateSelfReport,
  onRequestDycovue,
  onSendEmail,
  onEditMetadata,
  onViewDetails,
  onViewHistory,
  onRequestLabTest,
  onViewLabReport,
  labRefreshKey = 0,
  isSelected = false,
  onToggleSelect,
  onClaimStudy,
  onEditDycovueReport,
  currentUserId,
}) {
  const { patient, ...study } = studyData;
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const isSelfInProgress = study.selfReportStatus === 'in_progress' || study.selfReportStatus === 'draft';
  const [hasLabReport, setHasLabReport] = React.useState(false);
  const [labReportId, setLabReportId] = React.useState(null);

  const currentUserIdStr = currentUserId?.toString();
  const assignedId = (study.assignedRadiologist?._id || study.assignedRadiologist)?.toString();
  const isClaimedByMe = Boolean(assignedId && currentUserIdStr && assignedId === currentUserIdStr);
  const isClaimedByOther = Boolean(
    assignedId &&
    currentUserIdStr &&
    assignedId !== currentUserIdStr &&
    ['claimed', 'in_progress'].includes(study.dycovueReportStatus)
  );
  const assignedName =
    study.assignedRadiologist?.firstName
      ? `Dr. ${study.assignedRadiologist.firstName} ${study.assignedRadiologist.lastName || ''}`
      : study.assignedRadiologistName
      ? `Dr. ${study.assignedRadiologistName}`
      : 'Another Radiologist';

  React.useEffect(() => {
    let mounted = true;
    const uid = study.studyInstanceUID;
    const pid = patient?.patientId || study.patientId;
    if (uid) {
      labReportService.getForStudy(uid).then((res) => {
        if (mounted && res.data?.data) {
          setHasLabReport(true);
          setLabReportId(res.data.data.labResult || res.data.data._id);
        }
      }).catch(() => {
        if (pid) {
          labReportService.getForPatient(pid).then((res) => {
            if (mounted && res.data?.data && res.data.data.length > 0) {
              setHasLabReport(true);
              setLabReportId(res.data.data[0].labResult || res.data.data[0]._id);
            }
          }).catch(() => {});
        }
      });
    } else if (pid) {
      labReportService.getForPatient(pid).then((res) => {
        if (mounted && res.data?.data && res.data.data.length > 0) {
          setHasLabReport(true);
          setLabReportId(res.data.data[0].labResult || res.data.data[0]._id);
        }
      }).catch(() => {});
    }
    return () => { mounted = false; };
  }, [study.studyInstanceUID, patient?.patientId, study.patientId, labRefreshKey]);

  const [reportMenuAnchor, setReportMenuAnchor] = React.useState(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = React.useState(null);

  const isFlagged =
    study.dycovueReport?.facilityReview?.status === 'flagged' ||
    study.facilityReviewStatus === 'flagged';
  const hasCompletedReport = study.selfReportStatus === 'completed' || study.dycovueReportStatus === 'completed';
  const hasPendingReport = isSelfInProgress || ['requested', 'claimed', 'in_progress'].includes(study.dycovueReportStatus);

  // Consolidated Report Button Style
  const getReportButtonStyle = () => {
    if (isFlagged) {
      return {
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        borderColor: '#ef4444',
        color: '#ffffff',
        animation: 'pulse-red 2s infinite',
        '@keyframes pulse-red': {
          '0%, 100%': { boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.7)' },
          '50%': { boxShadow: '0 0 0 6px rgba(239, 68, 68, 0)' },
        },
      };
    }
    if (hasCompletedReport) {
      return {
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        borderColor: '#10b981',
        color: '#ffffff',
      };
    }
    if (hasPendingReport) {
      return {
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        borderColor: '#f59e0b',
        color: '#ffffff',
        animation: 'pulse-orange 2s infinite',
        '@keyframes pulse-orange': {
          '0%, 100%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.7)' },
          '50%': { boxShadow: '0 0 0 6px rgba(245, 158, 11, 0)' },
        },
      };
    }
    return {
      background: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.12)',
      borderColor: isDark ? 'rgba(59, 130, 246, 0.4)' : '#3b82f6',
      color: isDark ? '#60a5fa' : '#2563eb',
    };
  };

  const reportStyle = getReportButtonStyle();

  const moreButtonStyle = {
    width: 26,
    height: 26,
    padding: '2px',
    borderRadius: '4px',
    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
    color: isDark ? '#CBD5E1' : '#64748B',
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
      borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
      color: isDark ? '#FFFFFF' : '#0F172A',
      transform: 'scale(1.05)',
    },
  };

  return (
    <TableRow
      key={`study-${index}-${study.studyInstanceUID || study._id}`}
      hover
      selected={isSelected}
      sx={{
        '& > td': { py: '3px !important', px: '8px !important', fontSize: '0.75rem', height: '34px' },
        ...(study.isOrder ? { '& > td:first-of-type': { borderLeft: '3px solid rgba(129,140,248,0.5)' } } : { '& > td:first-of-type': { borderLeft: '3px solid transparent' } })
      }}
    >
      {onToggleSelect && (
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          <Checkbox size="small" checked={isSelected} onChange={() => onToggleSelect(studyData)} sx={{ p: 0.5 }} />
        </TableCell>
      )}
      <TableCell sx={{ fontSize: '0.75rem' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
          <Tooltip title={
            isClaimedByOther
              ? `Study claimed by ${assignedName} — Click to Review Lock`
              : isClaimedByMe
              ? 'Study claimed by you — Click to Write Report'
              : 'Click to Claim Study for Reporting'
          }>
            <Box
              component="span"
              onClick={() => {
                if (isClaimedByMe && onCreateSelfReport) {
                  onCreateSelfReport(studyData);
                } else if (onClaimStudy) {
                  onClaimStudy(studyData);
                }
              }}
              sx={{
                fontWeight: 600,
                fontSize: '0.78rem',
                cursor: 'pointer',
                color: isDark ? '#F1F5F9' : '#0F172A',
                transition: 'color 0.15s ease',
                '&:hover': {
                  color: '#3B82F6',
                  textDecoration: 'underline',
                },
              }}
            >
              {formatPatientName(patient.patientName)}
            </Box>
          </Tooltip>
          <Box sx={{ display: 'inline-flex', gap: '3px', alignItems: 'center' }}>
            {getStudyStatusChips(study, isClaimedByMe, isClaimedByOther, assignedName).map((chip, i) => (
              <Box
                key={i}
                component="span"
                onClick={() => {
                  if (chip.isClaimChip) {
                    if (isClaimedByMe && onCreateSelfReport) {
                      onCreateSelfReport(studyData);
                    } else if (onClaimStudy) {
                      onClaimStudy(studyData);
                    }
                  }
                }}
                sx={{
                  ...TAG,
                  backgroundColor: chip.bg,
                  color: chip.color,
                  ...(chip.isClaimChip ? {
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.85, transform: 'scale(1.04)' },
                    transition: 'all 0.15s ease',
                  } : {}),
                }}
              >
                {chip.label}
              </Box>
            ))}
            <TatCountdownChip
              tatDeadline={study.tatDeadline}
              tatStatus={study.tatStatus}
              dycovueReportStatus={study.dycovueReportStatus}
            />
          </Box>
        </Box>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
        {patient.patientId}
      </TableCell>
      
      {/* Date of Birth Column - Hidden on mobile */}
      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem', display: { xs: 'none', md: 'table-cell' } }}>
        {patient.patientBirthDate
          ? formatDicomDate(patient.patientBirthDate)
          : 'N/A'}
      </TableCell>
      
      {/* Gender Column - Hidden on extra small screens */}
      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem', display: { xs: 'none', sm: 'table-cell' } }}>
        {patient.patientSex === 'M' ? 'Male' : 
         patient.patientSex === 'F' ? 'Female' : 
         patient.patientSex === 'O' ? 'Other' :
         patient.patientSex || 'Unknown'}
      </TableCell>
      
      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
        <Typography variant="body2" noWrap sx={{ maxWidth: 160, fontSize: '0.75rem' }}>
          {study.studyDescription || t('dashboard.unnamedStudy')}
        </Typography>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {study.modality ? (
          <Box component="span" sx={{
            ...TAG,
            fontSize: '0.7rem',
            fontWeight: 800,
            px: '6px',
            py: '1.5px',
            borderRadius: '4px',
            backgroundColor: getModalityPalette(study.modality).bg,
            color: getModalityPalette(study.modality).color,
          }}>
            {study.modality}
          </Box>
        ) : <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>–</Box>}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
        {study.studyDate ? new Date(study.studyDate).toLocaleDateString() : 'N/A'}
      </TableCell>
      <TableCell align="center" sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem', width: 120, minWidth: 120, py: '4px !important', px: '6px !important' }}>
        <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" flexWrap="nowrap">
          {/* BUTTON 1: VIEW IMAGE */}
          <Tooltip title={isClaimedByOther ? `View Images in OHIF (Locked by ${assignedName})` : "View Images in OHIF Viewer"}>
            <span>
              <ModernIconButton 
                variant="view" 
                size="small" 
                onClick={() => onViewImages(study)} 
                disabled={!study.orthancStudyId && !study.studyInstanceUID}
                sx={{ width: 26, height: 26, padding: '2px', borderRadius: '4px' }}
              >
                <Visibility sx={{ fontSize: '0.9rem' }} />
              </ModernIconButton>
            </span>
          </Tooltip>

          {/* BUTTON 2: REPORTS MENU */}
          <Tooltip title={
            isFlagged
              ? 'Report Flagged with Concerns — Click to Review'
              : hasCompletedReport
              ? 'Completed Report Available — Click to View Reports'
              : hasPendingReport
              ? 'Report in Progress — Click for Report Options'
              : 'Reports & Dictation Menu (Self-Report, In-House, Lab)'
          }>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                // 1. If flagged, directly open the report in ReportViewer in read-only mode
                if (isFlagged) {
                  onViewReports(studyData, 'dycovue-report');
                  return;
                }
                // 2. If study is claimed by me or is an order:
                // An in-house report cannot be written (user reports it themselves).
                // If there's no completed in-house report to view, and no lab report:
                const canViewInHouse = study.dycovueReportStatus === 'completed';
                if ((study.isOrder || isClaimedByMe) && !canViewInHouse && !hasLabReport) {
                  if (study.selfReportStatus === 'completed') {
                    onViewReports(studyData, 'self-report');
                  } else if (onCreateSelfReport) {
                    onCreateSelfReport(studyData);
                  }
                  return;
                }
                setReportMenuAnchor(e.currentTarget);
              }}
              sx={{
                width: 26,
                height: 26,
                padding: '2px',
                borderRadius: '4px',
                border: '1px solid',
                transition: 'all 0.15s ease',
                ...reportStyle,
                '& .MuiSvgIcon-root': {
                  fontSize: '0.9rem',
                  color: reportStyle.color || 'inherit',
                  transition: 'all 0.15s ease',
                },
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                  background: reportStyle.background,
                  borderColor: reportStyle.borderColor,
                },
              }}
            >
              <Badge
                variant="dot"
                invisible={!isFlagged && !hasCompletedReport && !hasPendingReport}
                color={isFlagged ? 'error' : hasCompletedReport ? 'success' : 'warning'}
                sx={{
                  '& .MuiBadge-badge': {
                    width: 6,
                    height: 6,
                    minWidth: 6,
                    top: -2,
                    right: -2,
                  }
                }}
              >
                {isFlagged ? (
                  <Flag sx={{ fontSize: '0.9rem' }} />
                ) : (
                  <Description sx={{ fontSize: '0.9rem' }} />
                )}
              </Badge>
            </IconButton>
          </Tooltip>

          {/* BUTTON 3: VIEW PATIENT INFO */}
          <Tooltip title="View Patient Details & Metadata">
            <ModernIconButton 
              variant="info" 
              size="small" 
              onClick={() => onViewDetails(studyData)}
              sx={{ width: 26, height: 26, padding: '2px', borderRadius: '4px' }}
            >
              <InfoIcon sx={{ fontSize: '0.9rem' }} />
            </ModernIconButton>
          </Tooltip>

          {/* BUTTON 4: EXTRA / MORE ACTIONS */}
          <Tooltip title="More Actions">
            <IconButton
              size="small"
              onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
              sx={moreButtonStyle}
            >
              <MoreVert sx={{ fontSize: '0.9rem' }} />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* REPORT DROPDOWN MENU */}
        <Menu
          anchorEl={reportMenuAnchor}
          open={Boolean(reportMenuAnchor)}
          onClose={() => setReportMenuAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 8,
            sx: {
              borderRadius: '6px',
              minWidth: 290,
              backgroundColor: isDark ? '#1E1E1E' : '#FFFFFF',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
              py: 0.5,
            },
          }}
        >
          <Box sx={{ px: 2, py: 0.75, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? '#94A3B8' : '#64748B', fontSize: '0.68rem' }}>
              Select Report
            </Typography>
          </Box>

          {/* 1. Self-Report */}
          <MenuItem
            disabled={isClaimedByOther && study.selfReportStatus !== 'completed'}
            onClick={() => {
              if (isClaimedByOther && study.selfReportStatus !== 'completed') return;
              setReportMenuAnchor(null);
              if (study.selfReportStatus === 'completed') {
                onViewReports(studyData, 'self-report');
              } else if (onCreateSelfReport) {
                onCreateSelfReport(studyData);
              }
            }}
            sx={{
              py: 1,
              px: 1.75,
              gap: 1.5,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              <Description sx={{ fontSize: '1.15rem', color: study.selfReportStatus === 'completed' ? '#10B981' : '#3B82F6' }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: isDark ? '#F1F5F9' : '#0F172A' }}>
                  {study.selfReportStatus === 'completed'
                    ? 'View Self-Report (Clinician)'
                    : isSelfInProgress
                    ? 'Continue Self-Report (Draft)'
                    : 'Write Self-Report'}
                </Typography>
              }
              secondary={
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: isDark ? '#94A3B8' : '#64748B' }}>
                  {study.selfReportStatus === 'completed'
                    ? 'Signed & completed by facility'
                    : isSelfInProgress
                    ? 'Resume draft findings and impression'
                    : 'Enter clinical findings and impression'}
                </Typography>
              }
            />
            <Chip
              label={
                study.selfReportStatus === 'completed'
                  ? 'Completed'
                  : isSelfInProgress
                  ? 'Draft'
                  : 'Self-Report'
              }
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 700,
                borderRadius: '4px',
                bgcolor: study.selfReportStatus === 'completed'
                  ? 'rgba(16,185,129,0.15)'
                  : isSelfInProgress
                  ? 'rgba(245,158,11,0.15)'
                  : 'rgba(59,130,246,0.15)',
                color: study.selfReportStatus === 'completed'
                  ? '#10B981'
                  : isSelfInProgress
                  ? '#F59E0B'
                  : '#3B82F6',
              }}
            />
          </MenuItem>

          {/* 2. DycoVue In-House Report */}
          {((!study.isOrder && !study.orderId && !isClaimedByMe) || isFlagged || study.dycovueReportStatus === 'completed') && (
            <MenuItem
              onClick={() => {
                setReportMenuAnchor(null);
                if (isFlagged || study.dycovueReportStatus === 'completed' || ['requested', 'claimed', 'in_progress'].includes(study.dycovueReportStatus)) {
                  onViewReports(studyData, 'dycovue-report');
                } else if (onRequestDycovue) {
                  onRequestDycovue(studyData);
                }
              }}
              sx={{
                py: 1,
                px: 1.75,
                gap: 1.5,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                {isFlagged ? (
                  <Flag sx={{ fontSize: '1.15rem', color: '#EF4444' }} />
                ) : study.dycovueReportStatus === 'completed' ? (
                  <CheckCircle sx={{ fontSize: '1.15rem', color: '#10B981' }} />
                ) : ['requested', 'claimed', 'in_progress'].includes(study.dycovueReportStatus) ? (
                  <Schedule sx={{ fontSize: '1.15rem', color: '#F59E0B' }} />
                ) : (
                  <LocalHospital sx={{ fontSize: '1.15rem', color: '#10B981' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: isFlagged ? '#EF4444' : isDark ? '#F1F5F9' : '#0F172A' }}>
                    {isFlagged
                      ? 'Review Flagged Concerns'
                      : study.dycovueReportStatus === 'completed'
                      ? 'View In-House Report'
                      : study.dycovueReportStatus === 'in_progress'
                      ? 'In-House Report (In Progress)'
                      : study.dycovueReportStatus === 'claimed'
                      ? 'In-House Report (Claimed)'
                      : study.dycovueReportStatus === 'requested'
                      ? 'In-House Report (Awaiting Radiologist)'
                      : 'Request In-House DycoVue Report'}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: isDark ? '#94A3B8' : '#64748B' }}>
                    {isFlagged
                      ? 'Revision requested by facility — click to review'
                      : study.dycovueReportStatus === 'completed'
                      ? 'Official report completed — click to view'
                      : ['requested', 'claimed', 'in_progress'].includes(study.dycovueReportStatus)
                      ? 'Report being processed by DycoVue network'
                      : 'Dispatch to dedicated in-house radiologist'}
                  </Typography>
                }
              />
              <Chip
                label={
                  isFlagged
                    ? 'Flagged'
                    : study.dycovueReportStatus === 'completed'
                    ? 'Completed'
                    : study.dycovueReportStatus === 'in_progress'
                    ? 'In Progress'
                    : study.dycovueReportStatus === 'claimed'
                    ? 'Claimed'
                    : study.dycovueReportStatus === 'requested'
                    ? 'Requested'
                    : 'In-House'
                }
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  borderRadius: '4px',
                  bgcolor: isFlagged
                    ? '#EF4444'
                    : study.dycovueReportStatus === 'completed'
                    ? 'rgba(16,185,129,0.15)'
                    : ['requested', 'claimed', 'in_progress'].includes(study.dycovueReportStatus)
                    ? 'rgba(245,158,11,0.15)'
                    : 'rgba(16,185,129,0.12)',
                  color: isFlagged
                    ? '#FFFFFF'
                    : study.dycovueReportStatus === 'completed'
                    ? '#10B981'
                    : ['requested', 'claimed', 'in_progress'].includes(study.dycovueReportStatus)
                    ? '#F59E0B'
                    : '#10B981',
                }}
              />
            </MenuItem>
          )}

          {/* 3. Lab Report (only if report exists) */}
          {hasLabReport && (
            <MenuItem
              onClick={() => {
                setReportMenuAnchor(null);
                if (onViewLabReport) {
                  onViewLabReport(labReportId, formatPatientName(patient?.patientName));
                }
              }}
              sx={{
                py: 1,
                px: 1.75,
                gap: 1.5,
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <ScienceIcon sx={{ fontSize: '1.15rem', color: '#16A34A' }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: isDark ? '#F1F5F9' : '#0F172A' }}>
                    View Lab Report
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: isDark ? '#94A3B8' : '#64748B' }}>
                    Diagnostic lab findings available
                  </Typography>
                }
              />
              <Chip
                label="Ready"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  borderRadius: '4px',
                  bgcolor: 'rgba(22,163,74,0.15)',
                  color: '#16A34A',
                }}
              />
            </MenuItem>
          )}
        </Menu>

        {/* MORE OPTIONS DROPDOWN MENU */}
        <Menu
          anchorEl={moreMenuAnchor}
          open={Boolean(moreMenuAnchor)}
          onClose={() => setMoreMenuAnchor(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 8,
            sx: {
              borderRadius: '6px',
              minWidth: 220,
              backgroundColor: isDark ? '#1E1E1E' : '#FFFFFF',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
              py: 0.5,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setMoreMenuAnchor(null);
              onSendEmail(study);
            }}
            sx={{
              py: 0.85,
              px: 1.5,
              gap: 1.25,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 26 }}>
              <Send sx={{ fontSize: '0.95rem', color: '#FA709A' }} />
            </ListItemIcon>
            <ListItemText
              primary={<Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Send Email</Typography>}
              secondary={<Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Share study with doctor or patient</Typography>}
            />
          </MenuItem>

          <MenuItem
            onClick={() => {
              setMoreMenuAnchor(null);
              if (onViewHistory) onViewHistory(study);
            }}
            sx={{
              py: 0.85,
              px: 1.5,
              gap: 1.25,
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 26 }}>
              <HistoryIcon sx={{ fontSize: '0.95rem', color: '#8B5CF6' }} />
            </ListItemIcon>
            <ListItemText
              primary={<Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Patient History</Typography>}
              secondary={<Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Audit trail & prior exams</Typography>}
            />
          </MenuItem>
        </Menu></TableCell>
    </TableRow>
  );
});

function ClinicianDashboard() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    modality: 'all',
    year: '',
  });
  const [myAssignedOnly, setMyAssignedOnly] = useState(false);
  
  const debouncedSearch = useDebounce(filters.search, 500);
  const [error, setError] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const { mode, toggleTheme: sharedToggleTheme } = useThemeMode();
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [selfReportDialogOpen, setSelfReportDialogOpen] = useState(false);
  const [dycovueReportDialogOpen, setDycovueReportDialogOpen] = useState(false);
  const [selectedStudyForDycovueReport, setSelectedStudyForDycovueReport] = useState(null);
  const [serverStats, setServerStats] = useState(null);
  const [radStats, setRadStats] = useState(null);
  const [requestDycovueDialogOpen, setRequestDycovueDialogOpen] = useState(false);
  const [reportViewerDialogOpen, setReportViewerDialogOpen] = useState(false);
  const [reportViewerMode, setReportViewerMode] = useState('all');
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
  const [editMetadataDialogOpen, setEditMetadataDialogOpen] = useState(false);
  const [patientDetailDialogOpen, setPatientDetailDialogOpen] = useState(false);
  const [labTestDialogOpen, setLabTestDialogOpen] = useState(false);
  const [labTestStudy, setLabTestStudy] = useState(null);
  const [selectedLabReportId, setSelectedLabReportId] = useState(null);
  const [selectedLabPatientName, setSelectedLabPatientName] = useState('');
  const [labReportModalOpen, setLabReportModalOpen] = useState(false);
  const [labRefreshKey, setLabRefreshKey] = useState(0);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [serverStudies, setServerStudies] = useState([]);
  const [masterStudiesForCards, setMasterStudiesForCards] = useState([]);
  const [profilePictureTimestamp, setProfilePictureTimestamp] = useState(Date.now());
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [directMessageTargetUser, setDirectMessageTargetUser] = useState(null);
  const [directMessageContext, setDirectMessageContext] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  
  const studiesRef = React.useRef(null);
  const notificationButtonRef = React.useRef(null);
  const profileButtonRef = React.useRef(null);
  const searchInputRef = React.useRef(null);

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: sharedToggleTheme,
    }),
    [sharedToggleTheme],
  );

  const themeObj = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    }
  }, [isMobile]);

  const fetchStudyStats = useCallback(async () => {
    try {
      const data = await getStudyStatistics();
      if (data) setServerStats(data);
    } catch (err) {
      console.error('Failed to fetch study statistics:', err);
    }
  }, []);

  const fetchRadStats = useCallback(async () => {
    try {
      const data = await getMyRadiologistStats();
      if (data) setRadStats(data);
    } catch (err) {
      console.error('Failed to fetch radiologist stats:', err);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await getUnreadNotifications();
      setUnreadCount(data.count || 0);
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  useEffect(() => {
    // Theme sync (settings fetch + 'userSettingsUpdated') now lives in ThemeModeContext.
    const handleProfilePictureUpdate = () => {
      setProfilePictureTimestamp(Date.now());
      if (refreshUser) {
        refreshUser();
      }
    };

    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate);

    return () => {
      window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate);
    };
  }, [refreshUser]);

  const scrollToSection = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event?.currentTarget || profileButtonRef.current);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleNotificationMenuOpen = async (event) => {
    setNotificationAnchorEl(event?.currentTarget || notificationButtonRef.current);
    await fetchNotifications();
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationReceived = useCallback(async (data) => {
    await fetchNotifications();
    if (data?.type === 'lab_report_ready') {
      setLabRefreshKey(prev => prev + 1);
    }
  }, [fetchNotifications]);

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      await fetchNotifications();
      handleNotificationMenuClose();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const fetchScanBoardPage = useCallback(async (pageIndex, perPage, searchTerm, modality) => {
    try {
      setLoading(true);
      setError('');
      const offset = (pageIndex || 0) * (perPage || 10);
      const params = { 
        limit: perPage || 10, 
        offset,
        sort: '-createdAt' // FIXED: Sort by newest first
      };

      // Check for facilityId in URL (Admin viewing facility images)
      const queryParams = new URLSearchParams(location.search);
      const facilityId = queryParams.get('facilityId');
      if (facilityId) {
        params.facility = facilityId;
      }

      // Enhanced search handling for patient names
      if (searchTerm) {
        const cleanedSearch = String(searchTerm).trim();

        // DICOM format: replace spaces with ^
        const dicomFormat = cleanedSearch.replace(/\s+/g, '^');
        // Normal format: replace ^ with space
        const normalFormat = cleanedSearch.replace(/\^/g, ' ');

        // Send multiple search patterns to backend if it supports them
        params.search = cleanedSearch;
        params.searchPatterns = [cleanedSearch, dicomFormat, normalFormat];
      }
      if (modality && modality !== 'all') params.modality = modality;

      // Use scan-board specific endpoint which aggregates by Image documents
      const res = await getScanBoardStudies(params);
      const allStudies = Array.isArray(res.studies) ? res.studies : [];
      // Exclude pending orders — studies with no DICOM images yet belong in RIS only
      const validStudies = allStudies.filter(s => s.hasImages || (s.totalInstances || 0) > 0);
      setServerStudies(validStudies);
      if (!modality || modality === 'all') {
        setMasterStudiesForCards(validStudies);
      }
      if (typeof res.totalStudies === 'number') {
        setServerStats(prev => ({ ...(prev || {}), totalStudies: res.totalStudies }));
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching scan-board page:', err);
      setError('Failed to load studies: ' + (err.message || 'Unknown error'));
      setServerStudies([]);
      setLoading(false);
    }
  }, [location.search]);

  // Derive modality breakdown from master unfiltered studies (preserves card counts when filtering)
  const modalityBreakdownFromStudies = useMemo(() => {
    const sourceStudies = masterStudiesForCards.length > 0 ? masterStudiesForCards : serverStudies;
    if (!sourceStudies || sourceStudies.length === 0) return [];
    const counts = {};
    sourceStudies.forEach((s) => {
      const mod = s.modality || 'Other';
      counts[mod] = (counts[mod] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([_id, count]) => ({ _id, count }))
      .sort((a, b) => b.count - a.count);
  }, [masterStudiesForCards, serverStudies]);

  // NOTE: Search is now handled entirely by backend.
  // Backend searches across ALL studies before pagination.
  // Patient name search supports both formats: "Last First" and "Last^First"
  // Also searches patient ID and study description.

  const loadStudies = useCallback(async () => {
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchScanBoardPage(page, rowsPerPage, debouncedSearch, filters.modality);
      await fetchStudyStats();
      await fetchRadStats();
    } catch (err) {
      console.error('Error loading studies metadata:', err);
    }
  }, [fetchScanBoardPage, page, rowsPerPage, debouncedSearch, filters.modality, fetchStudyStats, fetchRadStats]);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await fetchScanBoardPage(0, rowsPerPage, '', '');
        await fetchStudyStats();
        await fetchRadStats();
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial dashboard load on mount / URL change
  }, [location.search]);

  useEffect(() => {
    setPage(0);
    fetchScanBoardPage(0, rowsPerPage, debouncedSearch, filters.modality);
  }, [debouncedSearch, filters.modality, fetchScanBoardPage, rowsPerPage]);

  useEffect(() => {
    if (user?._id) {
      fetchNotifications();
        const interval = setInterval(fetchNotifications, 5000);
      return () => clearInterval(interval);
    }
  }, [user?._id, fetchNotifications]);

  useEffect(() => {
    const handleOpenDirectMessage = (e) => {
      const detail = e?.detail || {};
      if (detail.user) {
        setDirectMessageTargetUser(detail.user);
        setDirectMessageContext(detail.context || null);
      }
      setReportViewerDialogOpen(false);
      setCurrentView('directMessages');
    };
    window.addEventListener('open-direct-message', handleOpenDirectMessage);
    return () => window.removeEventListener('open-direct-message', handleOpenDirectMessage);
  }, []);

  const handleSyncComplete = useCallback(async (data) => {
    try {
      if (data && typeof data === 'object' && 'addedImages' in data) {
        const { addedImages = 0, updatedImages = 0, deletedImages = 0, studyCreated = 0, studyUpdated = 0 } = data;
        if ((addedImages + updatedImages + deletedImages + studyCreated + studyUpdated) === 0) {
          return;
        }
      }

      await fetchScanBoardPage(page, rowsPerPage, debouncedSearch, filters.modality);
      await fetchStudyStats();
    } catch (err) {
      console.error('Error handling sync complete:', err);
    }
  }, [fetchScanBoardPage, page, rowsPerPage, debouncedSearch, filters.modality, fetchStudyStats]);

  // Periodic background live refresh (keeps study status, orders, and badges updated without page reloads)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchScanBoardPage(page, rowsPerPage, debouncedSearch, filters.modality);
      fetchStudyStats();
    }, 15000);
    return () => clearInterval(interval);
  }, [page, rowsPerPage, debouncedSearch, filters.modality, fetchScanBoardPage, fetchStudyStats]);

  const [selectedStudiesForBulk, setSelectedStudiesForBulk] = useState([]);
  const [bulkSendDialogOpen, setBulkSendDialogOpen] = useState(false);

  const handleToggleSelectStudy = (studyData) => {
    setSelectedStudiesForBulk((prev) => {
      const exists = prev.some((s) => s._id === studyData._id);
      return exists ? prev.filter((s) => s._id !== studyData._id) : [...prev, studyData];
    });
  };
  const handleOpenBulkSend = () => { if (selectedStudiesForBulk.length > 0) setBulkSendDialogOpen(true); };
  const handleClearBulkSelection = () => setSelectedStudiesForBulk([]);

  const [syncing, setSyncing] = useState(false);
  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await syncOrthanc();
      setUploadStatus(`Sync successful: ${res.message}`);
      await fetchScanBoardPage(0, rowsPerPage, debouncedSearch, filters.modality);
      await fetchStudyStats();
    } catch (err) {
      console.error('Sync error:', err);
      setError('Failed to sync with Orthanc: ' + (err.message || 'Unknown error'));
    } finally {
      setSyncing(false);
    }
  };

  const totalStudyCount = serverStats?.totalStudies ?? 0;

  const isFacilityRadiologist = user?.role === 'radiologist' && user?.radiologistType === 'facility';

  const paginatedStudies = React.useMemo(() => {
    let studies = serverStudies.map(study => ({
      ...study,
      patient: {
        patientId: study.patientId,
        patientName: study.patientName,
        patientSex: study.patientSex,
        patientBirthDate: study.patientBirthDate
      }
    }));

    // Facility radiologists can filter to only their assigned studies
    if (isFacilityRadiologist && myAssignedOnly) {
      const userId = user?._id?.toString();
      studies = studies.filter(s => {
        const assignee = s.assignedRadiologist;
        if (!assignee) return false;
        return (typeof assignee === 'object' ? assignee._id?.toString() : assignee?.toString()) === userId;
      });
    }

    // Filter by selected year
    if (filters.year && filters.year !== 'all' && String(filters.year).trim()) {
      const qYear = String(filters.year).trim();
      studies = studies.filter(s => {
        const d = s.studyDate ? new Date(s.studyDate) : (s.createdAt ? new Date(s.createdAt) : null);
        if (!d || isNaN(d.getTime())) return false;
        const fullYear = String(d.getFullYear());
        return qYear.length === 4 ? fullYear === qYear : fullYear.startsWith(qYear);
      });
    }

    // Search query matching (including 4-digit year match)
    if (filters.search && filters.search.trim()) {
      const q = filters.search.trim().toLowerCase();
      const isYearQuery = /^\d{4}$/.test(q);
      if (isYearQuery) {
        const yr = parseInt(q, 10);
        studies = studies.filter(s => {
          const d = s.studyDate ? new Date(s.studyDate) : (s.createdAt ? new Date(s.createdAt) : null);
          const matchesYear = d && !isNaN(d.getTime()) && d.getFullYear() === yr;
          const matchesText = (s.patientName || '').toLowerCase().includes(q) || (s.patientId || '').toLowerCase().includes(q) || (s.studyDescription || '').toLowerCase().includes(q);
          return matchesYear || matchesText;
        });
      }
    }

    return studies;
  }, [serverStudies, isFacilityRadiologist, myAssignedOnly, user?._id, filters.year, filters.search]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // FIXED: Use debouncedSearch instead of filters.search
    fetchScanBoardPage(newPage, rowsPerPage, debouncedSearch, filters.modality);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    // FIXED: Use debouncedSearch instead of filters.search
    fetchScanBoardPage(0, newRows, debouncedSearch, filters.modality);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const handleBackToAdmin = () => {
    navigate('/admin/facilities');
  };

  const handleMenuClick = (item) => {
    setCurrentView(item.key);
    if (item.action) {
      item.action();
    }
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const menuItems = [
    { key: 'patients', text: t('dashboard.scanBoard'), icon: <LocalHospital />, action: () => scrollToSection(studiesRef) },
    { key: 'directMessages', text: 'Messages', icon: <ChatIcon />, action: () => setCurrentView('directMessages') },
    { key: 'upload', text: t('dashboard.uploadStudies'), icon: <CloudUpload />, action: () => setUploadDialogOpen(true) },
    { key: 'mailHistory', text: t('dashboard.mailHistory'), icon: <Email />, action: () => setCurrentView('mailHistory') },
    { key: 'notifications', text: t('dashboard.notifications'), icon: <NotificationsIcon />, action: () => handleNotificationMenuOpen({ currentTarget: notificationButtonRef.current }) },
    { key: 'profile', text: t('dashboard.profile'), icon: <Person />, action: () => setCurrentView('profile') },
    { key: 'settings', text: t('dashboard.settings'), icon: <Settings />, action: () => setCurrentView('settings') },
    { key: 'help', text: t('dashboard.helpSupport'), icon: <Help />, action: () => setCurrentView('help') },
  ];

  const handleUpload = async (formData) => {
    setUploading(true);
    setError('');
    setUploadStatus('');

    try {
      const response = await uploadImage(formData);

      if (response && response.zipFile && response.images) {
        setUploadStatus(`ZIP processed successfully! ${response.totalFiles} DICOM files uploaded across ${response.studies} studies.`);
      } else if (response && response.conversionPerformed) {
        setUploadStatus('Image converted to DICOM format successfully!');
      } else if (response && response.image) {
        setUploadStatus('File uploaded successfully!');
      } else {
        setUploadStatus('Upload completed, but response format was unexpected.');
      }

      if (response && response.orthancStatus === 'success') {
        setUploadStatus(prev => prev + ' Files uploaded to Orthanc successfully!');
      } else if (response && response.orthancError) {
        setUploadStatus(prev => prev + ` Warning: ${response.orthancError}`);
      }

      setUploadDialogOpen(false);
      
      const isZipFile = response && response.zipFile;
      setTimeout(() => {
        fetchScanBoardPage(page, rowsPerPage, debouncedSearch, filters.modality);
        fetchStudyStats();
      }, isZipFile ? 2000 : 1000);
      
    } catch (err) {
      console.error('Upload error:', err);
      
      let errorMessage = 'Upload failed';
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      if (errorMessage.includes('zipFile')) {
        errorMessage = 'ZIP file processing failed. Please check the file format and try again.';
      } else if (errorMessage.includes('duplicate key')) {
        errorMessage = 'Some images may already exist in the database. Please check for duplicates.';
      }
      
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Open OHIF Viewer in new tab with better error handling
  const openOHIFViewer = (study) => {
    const studyInstanceUID = study.studyInstanceUID;
    
    const uid = studyInstanceUID || study.studyInstanceUID || study.studyUID || study.orthancStudyId;
    if (!uid) {
      alert(`Unable to open viewer: Study UID is missing. The study may still be processing.`);
      return false;
    }
    
    try {
      const ohifBaseUrl =
        process.env.REACT_APP_OHIF_URL ||
        window.env?.REACT_APP_OHIF_URL ||
        'http://localhost:8042/ohif';
      const viewerUrl = generateOHIFUrl(uid, ohifBaseUrl);

      console.log('Opening OHIF viewer:', viewerUrl);
      
      const newWindow = window.open(viewerUrl, '_blank', 'noopener,noreferrer,width=1400,height=900,scrollbars=yes,resizable=yes');
      if (newWindow) {
        newWindow.opener = null;
        try { newWindow.focus(); } catch (e) {}
        return true;
      } else {
        // Popup blocked: fallback to link click
        const link = document.createElement('a');
        link.href = viewerUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => { try { document.body.removeChild(link); } catch(e){} }, 100);
        return true;
      }
    } catch (error) {
      console.error('Error opening OHIF viewer:', error);
      const ohifBaseUrl =
        process.env.REACT_APP_OHIF_URL || 'http://localhost:8042/ohif';
      const uidFallback =
        studyInstanceUID ||
        study.studyInstanceUID ||
        study.studyUID ||
        study.orthancStudyId ||
        '';
      const viewerUrl = generateOHIFUrl(uidFallback, ohifBaseUrl);
      alert(`Could not open viewer automatically. Please copy and paste this URL in a new tab:\n\n${viewerUrl}`);
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(viewerUrl).catch(e => console.error('Clipboard error:', e));
      }
      return false;
    }
  };

  const handleCreateSelfReport = (study) => {
    setSelectedStudy(study);
    setPatientDetailDialogOpen(false);
    setEditMetadataDialogOpen(false);
    setSendEmailDialogOpen(false);
    setDycovueReportDialogOpen(false);
    setRequestDycovueDialogOpen(false);
    
    // If report is completed/approved, show ReportViewer with addendum button
    if (study.selfReportStatus === 'completed' || study.selfReportStatus === 'approved') {
      setReportViewerMode('self-report');
      setReportViewerDialogOpen(true);
    } else {
      setReportViewerDialogOpen(false);
      setSelfReportDialogOpen(true);
    }
  };

  const handleEditDycovueReport = (study) => {
    setSelectedStudy(study);
    setSelectedStudyForDycovueReport(study);
    setPatientDetailDialogOpen(false);
    setEditMetadataDialogOpen(false);
    setSendEmailDialogOpen(false);
    setReportViewerDialogOpen(false);
    setSelfReportDialogOpen(false);
    setRequestDycovueDialogOpen(false);
    setDycovueReportDialogOpen(true);
  };

  const handleRequestDycovue = (study) => {
    setSelectedStudy(study);
    setPatientDetailDialogOpen(false);
    setEditMetadataDialogOpen(false);
    setSendEmailDialogOpen(false);
    setDycovueReportDialogOpen(false);
    setReportViewerDialogOpen(false);
    setRequestDycovueDialogOpen(true);
  };

  const [historyPanel, setHistoryPanel] = useState({ open: false, study: null });
  const handleOpenHistory = (study) => setHistoryPanel({ open: true, study });
  const handleCloseHistory = () => setHistoryPanel({ open: false, study: null });

  const [claimModalState, setClaimModalState] = useState({ open: false, study: null });

  const handleOpenClaimModal = (study) => {
    const currentUserId = (user?._id || user?.id)?.toString();
    const assignedId = (study.assignedRadiologist?._id || study.assignedRadiologist)?.toString();
    const isClaimedByMe = Boolean(assignedId && currentUserId && assignedId === currentUserId);

    if (isClaimedByMe) {
      handleEditDycovueReport(study);
      return;
    }

    setClaimModalState({ open: true, study });
  };

  const handleConfirmClaimStudy = async (study) => {
    try {
      const studyId = study._id || study.pacsStudyId;
      await claimStudy(studyId);
      const currentUserId = user?._id || user?.id;
      const callerName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'You';
      const updatedStudy = {
        ...study,
        dycovueReportStatus: 'claimed',
        assignedRadiologist: currentUserId,
        assignedRadiologistName: callerName,
      };
      setServerStudies(prev => prev.map(s => {
        if ((s._id && s._id === study._id) || (s.studyInstanceUID && s.studyInstanceUID === study.studyInstanceUID)) {
          return {
            ...s,
            dycovueReportStatus: 'claimed',
            assignedRadiologist: currentUserId,
            assignedRadiologistName: callerName,
          };
        }
        return s;
      }));
      setClaimModalState({ open: false, study: null });
      handleCreateSelfReport(updatedStudy);
    } catch (err) {
      console.error('Failed to claim study:', err);
      throw err;
    }
  };

  const handleConfirmUnclaimStudy = async (study) => {
    try {
      const studyId = study._id || study.pacsStudyId;
      await unclaimStudy(studyId);
      setServerStudies(prev => prev.map(s => {
        if ((s._id && s._id === study._id) || (s.studyInstanceUID && s.studyInstanceUID === study.studyInstanceUID)) {
          return {
            ...s,
            dycovueReportStatus: 'requested',
            assignedRadiologist: null,
            assignedRadiologistName: null,
          };
        }
        return s;
      }));
      setClaimModalState({ open: false, study: null });
    } catch (err) {
      console.error('Failed to unclaim study:', err);
      throw err;
    }
  };

  const handleViewOnlyStudy = (study) => {
    openOHIFViewer(study);
  };

  const handleViewReports = (study, reportType = 'dycovue-report') => {
    setPatientDetailDialogOpen(false);
    setEditMetadataDialogOpen(false);
    setSendEmailDialogOpen(false);
    setDycovueReportDialogOpen(false);
    setSelfReportDialogOpen(false);
    setSelectedStudy(study);
    setReportViewerMode(reportType || 'dycovue-report');
    setReportViewerDialogOpen(true);
  };

  const handleViewImages = (study) => {
    const currentUserId = (user?._id || user?.id)?.toString();
    const assignedId = (study.assignedRadiologist?._id || study.assignedRadiologist)?.toString();
    const isClaimedByOther = Boolean(
      assignedId &&
      currentUserId &&
      assignedId !== currentUserId &&
      ['claimed', 'in_progress'].includes(study.dycovueReportStatus)
    );
    const isUnclaimed = !assignedId && (!study.dycovueReportStatus || ['unassigned', 'requested'].includes(study.dycovueReportStatus));

    // If claimed by another radiologist, show locked popup with view-only option
    if (isClaimedByOther) {
      setClaimModalState({ open: true, study });
      return;
    }

    // If unclaimed, prompt radiologist to claim & lock or view-only
    if (isUnclaimed && (study._id || study.pacsStudyId)) {
      setClaimModalState({ open: true, study });
      return;
    }

    // Otherwise, view directly
    const success = openOHIFViewer(study);
    if (!success) console.warn('Failed to open OHIF viewer for study:', study);
  };

  const handleSendEmail = (study) => {
    if (study.isOrder && !study.pacsStudyId && !study.hasImages) {
      setError('No images have been uploaded for this order yet. Please upload DICOM images before sending an email.');
      return;
    }
    // For orders linked to a study, use the study's _id so the backend can find it
    const studyForEmail = (study.isOrder && study.pacsStudyId)
      ? { ...study, _id: study.pacsStudyId }
      : study;
    setSelectedStudy(studyForEmail);
    setSendEmailDialogOpen(true);
  };

  const handleEditMetadata = (study) => {
    setSelectedStudy(study);
    setEditMetadataDialogOpen(true);
  };

  const handleViewDetails = (study) => {
    setSelectedStudy(study);
    setPatientDetailDialogOpen(true);
  };

  if (loading && !serverStudies.length) return (
    <Box sx={{ width: '100%', position: 'fixed', top: 0, left: 0 }}>
      <LinearProgress
        sx={{
          height: '3px',
          backgroundColor: 'transparent',
          '& .MuiLinearProgress-bar': {
            background: `linear-gradient(45deg, ${teslaColors.dark.primary}, ${teslaColors.dark.secondary})`,
          },
        }}
      />
    </Box>
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={themeObj}>
        <CssBaseline />
        {user?._id && (
          <NotificationListener 
            userId={user._id} 
            onNotificationReceived={handleNotificationReceived}
            onSyncComplete={handleSyncComplete}
          />
        )}
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: themeObj.palette.mode === 'dark' ? '#121212' : '#F5F5F7', position: 'relative', zIndex: 1 }}>
          <AppBar
            position="fixed"
            sx={{
              zIndex: themeObj.zIndex.drawer + 1,
              background: themeObj.palette.background.paper,
              boxShadow: 'none',
              borderBottom: `1px solid ${themeObj.palette.divider}`,
            }}
          >
            <Toolbar sx={{ minHeight: '48px !important', px: { xs: 1, sm: 2 } }}>
              <IconButton 
                onClick={handleDrawerToggle} 
                edge="start" 
                sx={{ 
                  mr: 2,
                  color: themeObj.palette.text.primary,
                  '&:hover': {
                    backgroundColor: themeObj.palette.action.hover,
                  }
                }}
              >
                {drawerOpen ? <ChevronLeft /> : <MenuIcon />}
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <FullLogo />
              </Box>
              {/* Switch to RIS button */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/clinician')}
                startIcon={
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    <path d="M12 11v6M9 14h6" />
                  </svg>
                }
                sx={{
                  ml: 2,
                  px: 1.5,
                  py: 0.5,
                  height: '30px',
                  borderColor: '#E2E8F0',
                  color: '#0D9488',
                  backgroundColor: '#F0FDFA',
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontFamily: '"Satoshi", sans-serif',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  '&:hover': {
                    borderColor: '#0D9488',
                    backgroundColor: '#CCFBF1',
                  }
                }}
              >
                Switch to RIS
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton 
                onClick={colorMode.toggleColorMode} 
                sx={{ 
                  mr: 1,
                  color: themeObj.palette.text.primary,
                  '&:hover': {
                    backgroundColor: themeObj.palette.action.hover,
                  }
                }}
              >
                {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
              <IconButton 
                ref={notificationButtonRef} 
                onClick={handleNotificationMenuOpen} 
                sx={{ 
                  mr: 1,
                  color: themeObj.palette.text.primary,
                  '&:hover': {
                    backgroundColor: themeObj.palette.action.hover,
                  }
                }}
              >
                <NotificationsIcon />
              </IconButton>
              <Menu
                anchorEl={notificationAnchorEl}
                open={Boolean(notificationAnchorEl)}
                onClose={handleNotificationMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    width: { xs: 'calc(100vw - 32px)', sm: 400 },
                    maxWidth: { xs: 'calc(100vw - 32px)', sm: 400 },
                    maxHeight: 520,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    borderRadius: '14px',
                    boxShadow: themeObj.palette.mode === 'dark'
                      ? '0 12px 36px rgba(0,0,0,0.6)'
                      : '0 12px 36px rgba(0,0,0,0.12)',
                    border: `1px solid ${themeObj.palette.divider}`,
                  },
                }}
              >
                <Box sx={{ p: 2, borderBottom: `1px solid ${themeObj.palette.divider}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                      Notifications
                    </Typography>
                    {unreadCount > 0 && (
                      <Button size="small" onClick={handleMarkAllAsRead} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
                        Mark all read
                      </Button>
                    )}
                  </Box>
                </Box>
                {notifications.length > 0 ? (
                  notifications.map((notification, index) => (
                    <MuiMenuItem
                      key={notification._id || index}
                      onClick={() => {
                        if (notification.type === 'lab_report_ready') {
                          setSelectedLabReportId(notification.labReportId || notification.labReportOrderId);
                          setSelectedLabPatientName(notification.patientName);
                          setLabReportModalOpen(true);
                        } else if (notification.studyUID) {
                          navigate(`/viewer/${notification.studyUID}?reportId=${notification.reportId}`);
                        } else {
                          navigate(`/dashboard?reportId=${notification.reportId}`);
                        }
                        handleNotificationMenuClose();
                      }}
                      sx={{
                        py: 1.5,
                        px: 2,
                        whiteSpace: 'normal',
                        alignItems: 'flex-start',
                        borderBottom: index < notifications.length - 1 ? `1px solid ${themeObj.palette.divider}` : 'none',
                        '&:hover': {
                          backgroundColor: themeObj.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Box sx={{ width: '100%', minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: notification.read ? 400 : 600,
                            mb: 0.5,
                            color: notification.type === 'report_flagged' ? '#EF4444' : 'inherit',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            lineHeight: 1.45,
                          }}
                        >
                          {notification.type === 'lab_report_ready' 
                            ? '🧪 ' 
                            : notification.type === 'report_flagged' 
                              ? '🚩 ' 
                              : (notification.priority === 'stat' ? '🚨 ' : '📋 ')}
                          {(notification.message || (notification.type === 'lab_report_ready' ? 'Lab Report Ready' : (notification.type === 'report_flagged' ? 'Report Concerns Flagged' : 'Report Ready'))).replace(/\^/g, ' ')}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          color="textSecondary"
                          sx={{
                            display: 'block',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.3,
                          }}
                        >
                          {formatPatientName(notification.patientName)} • {notification.type === 'lab_report_ready' ? 'Laboratory Department' : (notification.modality || 'Imaging')}
                        </Typography>
                        {notification.timestamp && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.72rem' }}>
                            {new Date(notification.timestamp).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    </MuiMenuItem>
                  ))
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      No new notifications
                    </Typography>
                  </Box>
                )}
              </Menu>
              <IconButton 
                ref={profileButtonRef} 
                onClick={handleProfileMenuOpen}
                sx={{
                  color: themeObj.palette.text.primary,
                  '&:hover': {
                    backgroundColor: themeObj.palette.action.hover,
                  }
                }}
              >
                {user?.profilePicture ? (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundImage: `url(${user.profilePicture}?t=${profilePictureTimestamp})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      border: '2px solid',
                      borderColor: themeObj.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                      boxShadow: themeObj.palette.mode === 'dark' 
                        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                        : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: 'primary.main',
                      fontSize: '0.875rem',
                      border: '2px solid',
                      borderColor: themeObj.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                      boxShadow: themeObj.palette.mode === 'dark' 
                        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                        : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </Avatar>
                )}
              </IconButton>
              <Menu
                anchorEl={profileAnchorEl}
                open={Boolean(profileAnchorEl)}
                onClose={handleProfileMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MuiMenuItem onClick={handleProfileMenuClose}>
                  {user?.profilePicture ? (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        mr: 2,
                        borderRadius: '50%',
                        backgroundImage: `url(${user.profilePicture}?t=${profilePictureTimestamp})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        border: '2px solid',
                        borderColor: themeObj.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  ) : (
                    <Avatar 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        mr: 2, 
                        bgcolor: theme.palette.primary.main,
                        border: '2px solid',
                        borderColor: themeObj.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      {user?.firstName?.charAt(0)}
                      {user?.lastName?.charAt(0)}
                    </Avatar>
                  )}
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {user?.firstName} {user?.lastName}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {user?.email}
                    </Typography>
                  </Box>
                </MuiMenuItem>
                <Divider />
                <MuiMenuItem
                  onClick={() => {
                    setCurrentView('profile');
                    handleProfileMenuClose();
                  }}
                >
                  <Person fontSize="small" sx={{ mr: 2 }} />
                  {t('dashboard.profile')}
                </MuiMenuItem>
                <MuiMenuItem
                  onClick={() => {
                    setChangePasswordOpen(true);
                    handleProfileMenuClose();
                  }}
                >
                  <KeyIcon fontSize="small" sx={{ mr: 2 }} />
                  {t('dashboard.changePassword')}
                </MuiMenuItem>
                <MuiMenuItem
                  onClick={() => {
                    setCurrentView('settings');
                    handleProfileMenuClose();
                  }}
                >
                  <Settings fontSize="small" sx={{ mr: 2 }} />
                  Settings
                </MuiMenuItem>
                <MuiMenuItem
                  onClick={() => {
                    navigate('/portal');
                    handleProfileMenuClose();
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '16px', opacity: 0.7 }}>
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                    <line x1="4" y1="22" x2="4" y2="15" />
                  </svg>
                  Switch Portal
                </MuiMenuItem>
                <MuiMenuItem onClick={handleLogout}>
                  <LogoutIcon fontSize="small" sx={{ mr: 2 }} />
                  Logout
                </MuiMenuItem>
              </Menu>
            </Toolbar>
          </AppBar>

          <StyledDrawer variant={isMobile ? 'temporary' : 'permanent'} open={drawerOpen} onClose={handleDrawerToggle}>
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: drawerOpen ? 1.5 : 0.75,
                pt: { xs: 7, sm: 7 },
                overflowX: 'hidden',
                boxSizing: 'border-box',
                position: 'relative',
              }}
            >
              {isMobile && (
                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    zIndex: 10,
                    opacity: 0.5,
                    '&:hover': {
                      opacity: 0.75,
                    },
                    display: { md: 'none' },
                  }}
                >
                  <Close />
                </IconButton>
              )}

              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  mt: 1,
                  minHeight: 0,
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)',
                    borderRadius: '4px',
                  },
                }}
              >
                <List sx={{ py: 0, px: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {menuItems.map((item) => {
                    const isActive = currentView === item.key;
                    const inactiveColor = mode === 'dark' ? '#CBD5E1' : '#6B7280';
                    const iconColor = isActive 
                      ? (mode === 'dark' ? themeObj.palette.primary.main : 'white')
                      : inactiveColor;
                    
                    return (
                      <Tooltip key={item.text} title={!drawerOpen ? item.text : ''} placement="right" arrow>
                        <StyledListItemButton 
                          selected={isActive} 
                          onClick={() => handleMenuClick(item)}
                          isDark={mode === 'dark'}
                          drawerOpen={drawerOpen}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: drawerOpen ? 36 : 0,
                              mr: drawerOpen ? 1.5 : 0,
                              color: iconColor,
                              justifyContent: 'center',
                              alignItems: 'center',
                              '& .MuiSvgIcon-root': {
                                fontSize: '1.25rem',
                              },
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          {drawerOpen && (
                            <ListItemText
                              primary={item.text}
                              primaryTypographyProps={{
                                fontWeight: isActive ? 600 : 500,
                                fontSize: '0.85rem',
                                color: isActive 
                                  ? (mode === 'dark' ? themeObj.palette.primary.main : 'white')
                                  : inactiveColor,
                              }}
                            />
                          )}
                        </StyledListItemButton>
                      </Tooltip>
                    );
                  })}
                </List>
              </Box>

              <Box sx={{ mt: 'auto', pt: 1, pb: 1, flexShrink: 0, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Divider sx={{ my: 1.5, width: '100%', borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)' }} />
                <Tooltip title={!drawerOpen ? t('dashboard.logout') : ''} placement="right" arrow>
                  <Button
                    onClick={handleLogout}
                    sx={{
                      width: drawerOpen ? 'calc(100% - 16px)' : 44,
                      minWidth: drawerOpen ? 'auto' : 44,
                      height: 44,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: drawerOpen ? 'flex-start' : 'center',
                      px: drawerOpen ? 2 : 0,
                      py: 1,
                      textTransform: 'none',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#EF4444',
                      borderRadius: '8px',
                      gap: drawerOpen ? 1.5 : 0,
                      '&:hover': {
                        backgroundColor: mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)',
                      },
                    }}
                  >
                    <LogoutIcon sx={{ fontSize: '1.25rem', color: '#EF4444' }} />
                    {drawerOpen && (
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#EF4444' }}>
                        {t('dashboard.logout')}
                      </Typography>
                    )}
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          </StyledDrawer>

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: { xs: 0.75, sm: 1 },
              width: { sm: `calc(100% - ${drawerOpen ? drawerWidth : collapsedDrawerWidth}px)` },
              transition: themeObj.transitions.create(['width', 'margin'], {
                easing: themeObj.transitions.easing.sharp,
                duration: themeObj.transitions.duration.enteringScreen,
              }),
            }}
          >
            {/* Spacer sized to the fixed AppBar so content never slides under it */}
            <Toolbar />
            {currentView === 'directMessages' ? (
              <Container maxWidth="xl" sx={{ position: 'relative', px: isSmallMobile ? 1.5 : 0 }}>
                <DirectMessagesView
                  initialTargetUser={directMessageTargetUser}
                  initialContext={directMessageContext}
                  onViewReport={(study, reportType) => handleViewReports(study, reportType)}
                />
              </Container>
            ) : currentView === 'messages' || currentView === 'mailHistory' ? (
              <MessageHistory />
            ) : currentView === 'profile' ? (
              <Container maxWidth="xl" sx={{ position: 'relative', px: isSmallMobile ? 1.5 : 0 }}>
                <ProfileSettings />
              </Container>
            ) : currentView === 'settings' ? (
              <Container maxWidth="xl" sx={{ position: 'relative', px: isSmallMobile ? 1.5 : 0 }}>
                <UserSettings />
              </Container>
            ) : currentView === 'help' ? (
              <Container maxWidth="xl" sx={{ position: 'relative', px: isSmallMobile ? 1.5 : 0 }}>
                <HelpSupport variant="pacs" />
              </Container>
            ) : (
              <Container maxWidth="xl" sx={{ position: 'relative', px: isSmallMobile ? 1.5 : 0 }}>
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    background: themeObj.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
                  }}
                  onClose={() => setError('')}
                >
                  {error}
                </Alert>
              )}

              {uploadStatus && (
                <Alert
                  severity="success"
                  sx={{
                    mb: 2,
                    borderRadius: '8px',
                    background: themeObj.palette.mode === 'dark' ? '#1E1E1E' : '#FFFFFF',
                  }}
                  onClose={() => setUploadStatus('')}
                >
                  {uploadStatus}
                </Alert>
              )}

              {/* Single Unified Clinical Workstation Container */}
              {(() => {
                const isDark = theme.palette.mode === 'dark';
                return (
                  <GlassPaper ref={studiesRef} sx={{ p: 0, overflow: 'hidden', borderRadius: '6px' }}>
                    {/* Facility Radiologist Toggle (if applicable) */}
                    {isFacilityRadiologist && (
                      <Box sx={{ p: '6px 10px', bgcolor: alpha(theme.palette.divider, 0.03), borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          variant={myAssignedOnly ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => setMyAssignedOnly(false)}
                          sx={{
                            borderRadius: '4px', textTransform: 'none', fontWeight: 600,
                            fontSize: '0.72rem', py: 0.2, px: 1.25, height: '26px',
                            ...(myAssignedOnly
                              ? {}
                              : {
                                  borderColor: teslaColors.dark.primary,
                                  color: teslaColors.dark.primary,
                                  '&:hover': { bgcolor: alpha(teslaColors.dark.primary, 0.08) },
                                }),
                          }}
                        >
                          All Facility Studies
                        </Button>
                        <Button
                          variant={myAssignedOnly ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => setMyAssignedOnly(true)}
                          sx={{
                            borderRadius: '4px', textTransform: 'none', fontWeight: 700,
                            fontSize: '0.72rem', py: 0.2, px: 1.25, height: '26px',
                            ...(myAssignedOnly
                              ? {
                                  bgcolor: '#0D9488',
                                  '&:hover': { bgcolor: '#0F766E' },
                                }
                              : {
                                  borderColor: '#0D9488',
                                  color: '#0D9488',
                                  '&:hover': { bgcolor: 'rgba(13,148,136,0.08)' },
                                }),
                          }}
                        >
                          My Assigned Studies
                        </Button>
                        {myAssignedOnly && (
                          <Typography variant="caption" sx={{ color: '#0D9488', fontWeight: 600, fontSize: '0.72rem' }}>
                            {paginatedStudies.length} assigned to you
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Facility Alert (if viewing for specific facility) */}
                    {location.search.includes('facilityId') && (
                      <Box p="6px 10px" display="flex" alignItems="center" justifyContent="space-between" borderBottom={`1px solid ${alpha(theme.palette.divider, 0.08)}`}>
                        <Alert 
                          severity="info" 
                          sx={{ flexGrow: 1, borderRadius: '4px', py: 0, '& .MuiAlert-message': { py: 0.25, fontSize: '0.75rem' } }}
                          icon={<Business sx={{ fontSize: '1rem' }} />}
                        >
                          Viewing images for facility: <strong>{new URLSearchParams(location.search).get('facilityName') || 'Selected Facility'}</strong>
                        </Alert>
                        {user?.role === 'admin' && (
                          <Button 
                            size="small" 
                            variant="contained"
                            color="primary"
                            onClick={handleBackToAdmin}
                            sx={{ ml: 1, borderRadius: '4px', height: '26px', fontSize: '0.72rem', py: 0 }}
                          >
                            Back to Admin
                          </Button>
                        )}
                      </Box>
                    )}

                    {/* UNIFIED TOP TOOLBAR: Modality Pills (Left) + Search & Actions (Right) */}
                    <Box
                      sx={{
                        p: '6px 10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                        gap: 1,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                      }}
                    >
                      {/* Modality Filter Tabs (Left) */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.6,
                          overflowX: 'auto',
                          py: 0.2,
                          maxWidth: { xs: '100%', md: '55%' },
                          '&::-webkit-scrollbar': { height: '3px' },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                            borderRadius: '2px',
                          },
                        }}
                      >
                        {/* "All" Tab */}
                        <Box
                          onClick={() => setFilters({ ...filters, modality: "all" })}
                          sx={{
                            cursor: "pointer",
                            py: 0.35,
                            px: 1,
                            borderRadius: "4px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.6,
                            whiteSpace: "nowrap",
                            transition: "all 0.15s ease",
                            background:
                              filters.modality === "all"
                                ? `linear-gradient(135deg, ${teslaColors.dark.primary} 0%, ${teslaColors.dark.secondary} 100%)`
                                : (isDark ? '#262626' : '#F1F5F9'),
                            border: filters.modality === "all" ? "1px solid transparent" : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
                            color: filters.modality === "all" ? "#FFF" : (isDark ? '#F1F5F9' : '#0F172A'),
                            "&:hover": {
                              borderColor: teslaColors.dark.primary,
                            },
                          }}
                        >
                          <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", lineHeight: 1 }}>
                            {modalityBreakdownFromStudies?.reduce((sum, m) => sum + m.count, 0) || 0}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.68rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                              opacity: filters.modality === "all" ? 1 : 0.75,
                              lineHeight: 1,
                            }}
                          >
                            All
                          </Typography>
                        </Box>

                        {/* Individual Modality Tabs */}
                        {modalityBreakdownFromStudies?.map((mod) => (
                          <Box
                            key={mod._id}
                            onClick={() => setFilters({ ...filters, modality: mod._id })}
                            sx={{
                              cursor: "pointer",
                              py: 0.35,
                              px: 1,
                              borderRadius: "4px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.6,
                              whiteSpace: "nowrap",
                              transition: "all 0.15s ease",
                              background:
                                filters.modality === mod._id
                                  ? `linear-gradient(135deg, ${teslaColors.dark.primary} 0%, ${teslaColors.dark.secondary} 100%)`
                                  : (isDark ? '#262626' : '#F1F5F9'),
                              border:
                                filters.modality === mod._id ? "1px solid transparent" : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
                              color:
                                filters.modality === mod._id ? "#FFF" : (isDark ? '#F1F5F9' : '#0F172A'),
                              "&:hover": {
                                borderColor: teslaColors.dark.primary,
                              },
                            }}
                          >
                            <Typography sx={{ fontWeight: 800, fontSize: "0.8rem", lineHeight: 1 }}>
                              {mod.count}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.68rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.4px",
                                opacity: filters.modality === mod._id ? 1 : 0.75,
                                lineHeight: 1,
                              }}
                            >
                              {mod._id || "Other"}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      {/* Search, Modality Dropdown, Refresh, and Upload Studies (Right) */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'nowrap',
                          width: { xs: '100%', md: 'auto' },
                          justifyContent: { xs: 'flex-start', md: 'flex-end' },
                        }}
                      >
                        {/* Search Field */}
                        <Tooltip title="Search by patient name (Last First or Last^First), patient ID, or study description">
                          <TextField
                            size="small"
                            placeholder={t('dashboard.searchPatientsStudies')}
                            variant="outlined"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            inputRef={searchInputRef}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      try { searchInputRef.current?.focus(); } catch (e) {}
                                      try {
                                        fetchScanBoardPage(0, rowsPerPage, filters.search, filters.modality);
                                      } catch (e) {}
                                    }}
                                    sx={{ p: 0.2 }}
                                  >
                                    <Search sx={{ color: 'text.secondary', fontSize: '0.95rem' }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                              endAdornment: filters.search && (
                                <IconButton
                                  size="small"
                                  onClick={() => setFilters({ ...filters, search: '' })}
                                  sx={{ p: 0.2 }}
                                >
                                  <Close sx={{ fontSize: '0.85rem' }} />
                                </IconButton>
                              ),
                            }}
                            sx={{
                              width: { xs: '100%', sm: 220, md: 240 },
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '4px',
                                height: '30px',
                                fontSize: '0.75rem',
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                  borderColor: teslaColors.dark.primary,
                                },
                                '&.Mui-focused': {
                                  borderColor: teslaColors.dark.secondary,
                                  boxShadow: `0 0 0 2px ${alpha(teslaColors.dark.primary, 0.2)}`,
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                py: 0.5,
                                px: 0.75,
                              }
                            }}
                          />
                        </Tooltip>

                        {/* Modality Dropdown */}
                        <FormControl size="small" sx={{ width: { xs: 90, sm: 110 } }}>
                          <Select
                            value={filters.modality}
                            displayEmpty
                            onChange={(e) => setFilters({ ...filters, modality: e.target.value })}
                            sx={{
                              borderRadius: '4px',
                              height: '30px',
                              fontSize: '0.75rem',
                              '& .MuiSelect-select': { py: 0.5, px: 1 },
                            }}
                          >
                            <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>All Mods</MenuItem>
                            <MenuItem value="CT" sx={{ fontSize: '0.75rem' }}>CT</MenuItem>
                            <MenuItem value="MR" sx={{ fontSize: '0.75rem' }}>MRI</MenuItem>
                            <MenuItem value="XR" sx={{ fontSize: '0.75rem' }}>X-Ray</MenuItem>
                            <MenuItem value="US" sx={{ fontSize: '0.75rem' }}>Ultrasound</MenuItem>
                            <MenuItem value="CR" sx={{ fontSize: '0.75rem' }}>CR</MenuItem>
                            <MenuItem value="DR" sx={{ fontSize: '0.75rem' }}>DR</MenuItem>
                            <MenuItem value="MG" sx={{ fontSize: '0.75rem' }}>Mammography</MenuItem>
                            <MenuItem value="OT" sx={{ fontSize: '0.75rem' }}>Other</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Year Input Filter */}
                        <TextField
                          size="small"
                          placeholder="Year"
                          value={filters.year === 'all' ? '' : (filters.year || '')}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                            setFilters({ ...filters, year: val });
                          }}
                          inputProps={{
                            maxLength: 4,
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                          }}
                          InputProps={{
                            sx: {
                              borderRadius: '4px',
                              height: '30px',
                              fontSize: '0.75rem',
                              width: { xs: 80, sm: 95 },
                              '& input': {
                                py: 0.5,
                                px: 1,
                              },
                            },
                            endAdornment: (filters.year && filters.year !== 'all') ? (
                              <InputAdornment position="end" sx={{ mr: -0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => setFilters({ ...filters, year: '' })}
                                  sx={{ p: 0.25 }}
                                >
                                  <Close sx={{ fontSize: 13 }} />
                                </IconButton>
                              </InputAdornment>
                            ) : null,
                          }}
                        />

                        {/* Refresh Button */}
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Refresh sx={{ fontSize: '0.9rem' }} />}
                          onClick={() => {
                            fetchScanBoardPage(page, rowsPerPage, debouncedSearch, filters.modality);
                            fetchStudyStats();
                            fetchRadStats();
                          }}
                          disabled={loading}
                          sx={{ 
                            height: '30px',
                            borderRadius: '4px',
                            borderColor: alpha(theme.palette.divider, 0.2),
                            color: theme.palette.text.primary,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            px: 1.25,
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: teslaColors.dark.primary,
                              backgroundColor: alpha(teslaColors.dark.primary, 0.08),
                            }
                          }}
                        >
                          {t('common.refresh')}
                        </Button>

                        {/* Upload Studies Button */}
                        <GlowButton 
                          startIcon={<CloudUpload sx={{ fontSize: '0.95rem' }} />} 
                          onClick={() => setUploadDialogOpen(true)} 
                          sx={{
                            height: '30px',
                            py: 0,
                            px: 1.5,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            borderRadius: '4px',
                            whiteSpace: 'nowrap',
                            textTransform: 'none',
                          }}
                        >
                          {loading && !serverStats ? (
                            t('dashboard.uploadStudies')
                          ) : (
                            `Upload (${serverStats?.totalStudies || 0})`
                          )}
                        </GlowButton>

                        {/* Avg Report Turnaround Time (TAT) */}
                        <Chip
                          label={`Avg TAT: ${formatTatMinutes(radStats?.avgTATMinutes)}`}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontWeight: 700, fontSize: '0.68rem', height: 22, whiteSpace: 'nowrap' }}
                        />
                      </Box>
                    </Box>

                    {loading && !paginatedStudies.length && (
                      <Box sx={{ width: '100%' }}>
                        <LinearProgress sx={{ height: 2 }} />
                      </Box>
                    )}

                    {(paginatedStudies && paginatedStudies.length > 0) ? (
                      <>
                        {/* Bulk send toolbar */}
                        {selectedStudiesForBulk.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '4px 10px', bgcolor: '#EFF6FF', borderBottom: '1px solid #BFDBFE' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1D4ED8', flex: 1, fontSize: '0.75rem' }}>
                              {selectedStudiesForBulk.length} {selectedStudiesForBulk.length === 1 ? 'study' : 'studies'} selected
                            </Typography>
                            <Button size="small" onClick={handleClearBulkSelection} sx={{ color: '#6B7280', fontWeight: 600, textTransform: 'none', fontSize: '0.72rem', py: 0 }}>
                              Clear
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<DoneAll sx={{ fontSize: '0.85rem' }} />}
                              onClick={handleOpenBulkSend}
                              disabled={selectedStudiesForBulk.length > 10}
                              sx={{ bgcolor: '#0D9488', '&:hover': { bgcolor: '#00665C' }, textTransform: 'none', fontWeight: 700, borderRadius: '4px', fontSize: '0.72rem', py: 0.2, height: '24px' }}
                            >
                              {selectedStudiesForBulk.length > 10 ? 'Max 10 studies' : `Bulk Send (${selectedStudiesForBulk.length})`}
                            </Button>
                          </Box>
                        )}

                        <TableContainer
                          sx={{
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            maxWidth: '100%',
                            '&::-webkit-scrollbar': { height: '5px' },
                            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
                          }}
                        >
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow sx={{ '& th': { py: '5px !important', px: '8px !important', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', bgcolor: isDark ? '#1E1E1E' : '#FFFFFF' } }}>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    indeterminate={selectedStudiesForBulk.length > 0 && selectedStudiesForBulk.length < paginatedStudies.length}
                                    checked={paginatedStudies.length > 0 && selectedStudiesForBulk.length === paginatedStudies.length}
                                    onChange={(e) => {
                                      if (e.target.checked) setSelectedStudiesForBulk(paginatedStudies);
                                      else setSelectedStudiesForBulk([]);
                                    }}
                                    sx={{ p: 0.5 }}
                                  />
                                </TableCell>
                                <TableCell><strong>Patient Name</strong></TableCell>
                                <TableCell><strong>Patient ID</strong></TableCell>
                                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Date of Birth</strong></TableCell>
                                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}><strong>Gender</strong></TableCell>
                                <TableCell><strong>Study Description</strong></TableCell>
                                <TableCell><strong>Modality</strong></TableCell>
                                <TableCell><strong>Study Date</strong></TableCell>
                                <TableCell align="center" sx={{ width: 120, minWidth: 120 }}><strong>Actions</strong></TableCell>
                              </TableRow>
                            </TableHead>
                        <TableBody>
                          {paginatedStudies.map((studyData, studyIndex) => (
                            <StudyRow
                              key={`study-${studyIndex}-${studyData.studyInstanceUID || studyData._id}`}
                              studyData={studyData}
                              index={studyIndex}
                              t={t}
                              isMobile={isMobile}
                              formatPatientName={formatPatientName}
                              onViewImages={handleViewImages}
                              onViewReports={handleViewReports}
                              onCreateSelfReport={handleCreateSelfReport}
                              onRequestDycovue={handleRequestDycovue}
                              onSendEmail={handleSendEmail}
                              onEditMetadata={handleEditMetadata}
                              onViewDetails={handleViewDetails}
                              onViewHistory={handleOpenHistory}
                              onRequestLabTest={(s) => {
                                setLabTestStudy(s);
                                setLabTestDialogOpen(true);
                              }}
                              onViewLabReport={(reportId, patientName) => {
                                setSelectedLabReportId(reportId);
                                setSelectedLabPatientName(patientName);
                                setLabReportModalOpen(true);
                              }}
                              labRefreshKey={labRefreshKey}
                              isSelected={selectedStudiesForBulk.some((s) => s._id === studyData._id)}
                              onToggleSelect={handleToggleSelectStudy}
                              currentUserId={user?._id || user?.id}
                              onClaimStudy={handleOpenClaimModal}
                              onEditDycovueReport={handleEditDycovueReport}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={totalStudyCount}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[10, 20, 25, 50, 100]}
                      labelRowsPerPage="Rows per page:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`}
                      sx={{
                        borderTop: `1px solid ${themeObj.palette.divider}`,
                        minHeight: '34px',
                        '& .MuiTablePagination-toolbar': {
                          minHeight: '34px',
                          px: { xs: 1, sm: 2 },
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                  </>
                ) : (
                  <Box textAlign="center" py={8}>
                    <LocalHospital sx={{ fontSize: isMobile ? 48 : 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="textSecondary" gutterBottom>
                      No DICOM studies found
                    </Typography>
                    <Typography variant="body2" color="textSecondary" mb={3}>
                      Upload DICOM files, ZIP archives, or images to get started
                    </Typography>
                    <Button variant="contained" startIcon={<CloudUpload />} onClick={() => setUploadDialogOpen(true)} sx={{ borderRadius: '12px' }}>
                      Upload First Study
                    </Button>
                  </Box>
                )}
              </GlassPaper>
            );
          })()}
        </Container>
      )}

              {(selectedStudy || selectedStudyForDycovueReport) && (
                <>
                  {selectedStudy && (
                    <PatientDetailDialog
                      open={patientDetailDialogOpen}
                      onClose={() => {
                        setPatientDetailDialogOpen(false);
                        setSelectedStudy(null);
                      }}
                      study={selectedStudy}
                      onRequestLabTest={(s) => {
                        setLabTestStudy(s);
                        setLabTestDialogOpen(true);
                      }}
                      onEditMetadata={(s) => {
                        setSelectedStudy(s);
                        setEditMetadataDialogOpen(true);
                      }}
                    />
                  )}
                  
                  {selectedStudy && (
                    <CreateSelfReport
                      open={selfReportDialogOpen}
                      onClose={() => {
                        setSelfReportDialogOpen(false);
                        setSelectedStudy(null);
                      }}
                      study={selectedStudy}
                      onSuccess={() => {
                        setSelfReportDialogOpen(false);
                        loadStudies();
                      }}
                    />
                  )}

                  <DycovueReportForm
                    open={dycovueReportDialogOpen}
                    onClose={() => {
                      setDycovueReportDialogOpen(false);
                      setSelectedStudyForDycovueReport(null);
                      setSelectedStudy(null);
                    }}
                    study={selectedStudyForDycovueReport || selectedStudy}
                    onSuccess={() => {
                      setDycovueReportDialogOpen(false);
                      setSelectedStudyForDycovueReport(null);
                      setSelectedStudy(null);
                      fetchScanBoardPage(page, rowsPerPage, debouncedSearch, filters.modality);
                    }}
                  />

                  <RequestDycovueReader
                    open={requestDycovueDialogOpen}
                    onClose={() => {
                      setRequestDycovueDialogOpen(false);
                      setSelectedStudy(null);
                    }}
                    study={selectedStudy}
                    onSuccess={() => {
                      setRequestDycovueDialogOpen(false);
                      loadStudies();
                    }}
                  />

                  <SendEmailDialog
                    open={sendEmailDialogOpen}
                    onClose={() => {
                      setSendEmailDialogOpen(false);
                      setSelectedStudy(null);
                    }}
                    study={selectedStudy}
                  />

                  <EditPatientMetadataDialog
                    open={editMetadataDialogOpen}
                    onClose={() => {
                      setEditMetadataDialogOpen(false);
                      setSelectedStudy(null);
                    }}
                    study={selectedStudy}
                    onSuccess={(updatedStudy) => {
                      setEditMetadataDialogOpen(false);
                      fetchScanBoardPage(page, rowsPerPage, debouncedSearch, filters.modality);
                    }}
                  />

                  <Dialog
                    open={reportViewerDialogOpen}
                    onClose={() => {
                      setReportViewerDialogOpen(false);
                      setSelectedStudy(null);
                      setReportViewerMode('all');
                    }}
                    maxWidth={false}
                    fullWidth
                    PaperProps={{
                      sx: {
                        borderRadius: '12px',
                        width: { xs: '95vw', sm: '92vw', md: '860px', lg: '920px' },
                        maxWidth: '940px !important',
                        maxHeight: '88vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                      }
                    }}
                  >
                    <DialogTitle sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}` }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1.25}>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.98rem' }}>
                            {reportViewerMode === 'self-report'
                              ? 'Self-Report (Clinician)'
                              : reportViewerMode === 'dycovue-report'
                              ? 'DycoVue In-House Report'
                              : 'Study Reports'}
                          </Typography>
                          {selectedStudy?.patientName && (
                            <Chip
                              label={formatPatientName(selectedStudy.patientName)}
                              size="small"
                              sx={{ fontWeight: 600, fontSize: '0.72rem', height: 20 }}
                            />
                          )}
                          {selectedStudy?.modality && (
                            <Chip
                              label={selectedStudy.modality}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 700, fontSize: '0.72rem', height: 20 }}
                            />
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setReportViewerDialogOpen(false);
                            setSelectedStudy(null);
                            setReportViewerMode('all');
                          }}
                        >
                          <Close sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    </DialogTitle>
                    <DialogContent sx={{ p: { xs: 1.5, sm: 2 }, overflowY: 'auto', flexGrow: 1 }}>
                      <ReportViewer
                        studyId={selectedStudy?._id || selectedStudy?.pacsStudyId || selectedStudy?.studyInstanceUID}
                        study={selectedStudy}
                        reportType={reportViewerMode}
                        onReportTypeChange={(newType) => setReportViewerMode(newType)}
                        onEditDycovueReport={(s) => {
                          setReportViewerDialogOpen(false);
                          handleEditDycovueReport(s || selectedStudy);
                        }}
                      />
                    </DialogContent>
                  </Dialog>
                </>
              )}

              {/* Patient History Drawer */}
              <PatientHistoryPanel
                open={historyPanel.open}
                onClose={handleCloseHistory}
                patientId={historyPanel.study?.patientId || historyPanel.study?.patientMrn}
                patientName={historyPanel.study?.patientName}
                patientBirthDate={historyPanel.study?.patientBirthDate}
                patientSex={historyPanel.study?.patientSex}
                currentStudyId={historyPanel.study?._id}
              />

              {/* Bulk send dialog — outside selectedStudy guard so it always renders */}
              <SendEmailDialog
                open={bulkSendDialogOpen}
                onClose={() => {
                  setBulkSendDialogOpen(false);
                  setSelectedStudiesForBulk([]);
                }}
                studies={selectedStudiesForBulk}
              />

              <EnhancedUploadDialog
                open={uploadDialogOpen}
                onClose={() => setUploadDialogOpen(false)}
                onUpload={handleUpload}
                uploading={uploading}
                error={error}
              />

              <ChangePasswordDialog 
                open={changePasswordOpen} 
                onClose={() => setChangePasswordOpen(false)} 
              />

          {/* Request Lab Test Dialog */}
          {labTestStudy && (
            <RequestLabTestDialog
              open={labTestDialogOpen}
              onClose={() => {
                setLabTestDialogOpen(false);
                setLabTestStudy(null);
              }}
              patientId={labTestStudy.patient?.patientId || labTestStudy.patientId}
              patientName={formatPatientName(labTestStudy.patient?.patientName || labTestStudy.patientName)}
              studyUID={labTestStudy.studyInstanceUID}
              isDark={mode === 'dark'}
            />
          )}

          {/* View Lab Report Modal */}
          <LabReportModal
            open={labReportModalOpen}
            onClose={() => setLabReportModalOpen(false)}
            resultId={selectedLabReportId}
            patientName={selectedLabPatientName}
            isDark={mode === 'dark'}
          />

          {/* Claim Study Modal */}
          <ClaimStudyModal
            open={claimModalState.open}
            onClose={() => setClaimModalState({ open: false, study: null })}
            study={claimModalState.study}
            currentUserId={user?._id || user?.id}
            onConfirmClaim={handleConfirmClaimStudy}
            onUnclaim={handleConfirmUnclaimStudy}
            onViewOnly={handleViewOnlyStudy}
            isDark={mode === 'dark'}
          />
        </Box>
      </Box>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default ClinicianDashboard;
